<template>
    <div :class="[$style.commissionCard, $style.clickable]" @click="handleCardClick" @blur="removeHover" tabindex="0">
        <div :class="$style.commissionInfo">
            <!-- 썸네일 섹션 -->
            <div :class="$style.contentSection">
                <div :class="$style.thumbnailContainer">
                    <img :src="commission.thumbnailUrl" :alt="commission.name" :class="$style.thumbnail" />
                </div>

                <div :class="$style.commissionDetails">
                    <div :class="$style.headerRow">
                        <span :class="[$style.categoryBadge, $style[`cat-${commission.category}`]]">
                            {{ categoryLabel(commission.category) }}
                        </span>
                    </div>
                    <h3 :class="$style.commissionName">{{ commission.name }}</h3>
                    <p v-if="commission.description" :class="$style.commissionDescription">
                        {{ commission.description }}
                    </p>
                </div>

                <!-- 찜 버튼 -->
                <div :class="$style.likeButtonContainer">
                    <button v-if="showLikeButton" :class="[$style.likeButton, { [$style.liked]: isLiked }]"
                        @click.stop="handleLikeClick" :disabled="disabled">
                        <img v-if="isLiked" :class="$style.likeHeart" src="@/assets/images/heart.png" alt="찜됨" />
                        <img v-else :class="$style.likeHeart" src="@/assets/images/heart_gray.png" alt="찜하기" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CommissionCard",
    props: {
        commission: {
            type: Object,
            required: true,
            validator(commission) {
                return commission && typeof commission.id === "string";
            },
        },
        isLiked: {
            type: Boolean,
            default: false,
        },
        showLikeButton: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["like-toggle"],
    methods: {
        categoryLabel(value) {
            const map = {
                mixing: "믹싱",
                illustration: "일러스트",
                design: "디자인",
                video: "영상",
            };
            return map[value] || "기타";
        },
        handleCardClick(event) {
            this.$router.push({ name: "CommissionDetail", params: { id: this.commission.id } });
            // 클릭 후 포커스 제거하여 파란 음영 없애기
            event.target.blur();
        },
        handleLikeClick() {
            this.$emit("like-toggle", this.commission.id);
        },
        removeHover() {
            // 포커스가 제거될 때 호버 상태도 제거
        },
    },
};
</script>

<style module>
.commissionCard {
    display: grid;
    grid-template-columns: 1fr;
    padding: 10px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    position: relative;
}

.commissionCard:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.commissionCard.clickable {
    cursor: pointer;
}

.commissionCard.clickable:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(102, 189, 255, 0.2);
    border: 1px solid rgba(102, 189, 255, 0.3);
}

.commissionCard.clickable:focus {
    outline: none;
}

.commissionInfo {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.contentSection {
    gap: 10px;
    display: flex;
    align-items: center;
    flex-direction: column;
}

.thumbnailContainer {
    width: 100%;
    max-width: 200px;
    height: 200px;
    overflow: hidden;
    border-radius: 8px;
    background-color: #f5f5f5;
    flex-shrink: 0;
}

.thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.commissionDetails {
    flex: 1;
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 8px;
    margin: auto;
    width: 100%;
}

.headerRow {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;
}

.categoryBadge {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    font-family: "NanumSquareRoundB", sans-serif;
    color: #fff;
    white-space: nowrap;
    transition: all 0.2s ease;
}

.cat-mixing {
    background: #8e44ad;
}

.cat-illustration {
    background: #e67e22;
}

.cat-design {
    background: #16a085;
}

.cat-video {
    background: #2980b9;
}

.commissionName {
    margin: 0;
    font-size: 1rem;
    font-weight: bold;
    color: #333;
    font-family: "NanumSquareRoundB", sans-serif;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.commissionDescription {
    margin: 4px 0 0 0;
    font-size: 0.8rem;
    color: #666;
    font-family: "NanumSquareRoundR", sans-serif;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    text-align: center;
}

.likeButtonContainer {
    margin: auto;
}

/* 찜 버튼 스타일 */
.likeButton {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
}

.likeHeart {
    width: 22px;
    height: 22px;
    transition: all 0.2s ease;
}

.likeButton:hover {
    background: rgba(255, 107, 157, 0.1);
    transform: scale(1.1);
}

.likeButton:hover .likeHeart {
    transform: scale(1.1);
}

.likeButton.liked {
    animation: heartBeat 0.6s ease-in-out;
}

.likeButton:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@keyframes heartBeat {
    0% {
        transform: scale(1);
    }

    14% {
        transform: scale(1.3);
    }

    28% {
        transform: scale(1);
    }

    42% {
        transform: scale(1.3);
    }

    70% {
        transform: scale(1);
    }
}

/* ========================================
   반응형 디자인
   - Large: 1025px ~ (기본 스타일)
   - Medium: 481px ~ 1024px
   - Small: ~ 480px
   ======================================== */

/* Medium (481px ~ 1024px) */
@media (max-width: 1024px) {
    .commissionCard {
        padding: 8px;
    }

    .contentSection {
        flex-direction: row;
        align-items: center;
        text-align: center;
    }

    .thumbnailContainer {
        width: 100px;
        height: 100px;
        min-width: 100px;
        max-width: 100px;
        border-radius: 8px;
    }

    .commissionDetails {
        width: 100%;
        text-align: left;
    }

    .headerRow {
        justify-content: flex-start;
        gap: 6px;
    }

    .commissionName {
        text-align: left;
        font-size: 0.95rem;
    }

    .commissionDescription {
        text-align: left;
        font-size: 0.75rem;
    }

    .likeButton {
        margin-right: 10px;
        width: 36px;
        height: 36px;
    }

    .likeHeart {
        width: 20px;
        height: 20px;
    }
}

/* Small (~ 480px) */
@media (max-width: 480px) {
    .commissionCard {
        padding: 8px;
        border-radius: 8px;
    }

    .commissionInfo {
        gap: 10px;
    }

    .contentSection {
        flex-direction: row;
        align-items: center;
        gap: 8px;
    }

    .thumbnailContainer {
        width: 70px;
        height: 70px;
        min-width: 70px;
        max-width: 70px;
        border-radius: 6px;
    }

    .commissionDetails {
        width: 100%;
        text-align: left;
        gap: 4px;
    }

    .headerRow {
        justify-content: flex-start;
        gap: 4px;
    }

    .categoryBadge {
        font-size: 0.6rem;
        padding: 3px 8px;
    }

    .commissionName {
        text-align: left;
        font-size: 0.85rem;
    }

    .commissionDescription {
        text-align: left;
        font-size: 0.7rem;
        margin: 2px 0 0 0;
        -webkit-line-clamp: 1;
        line-clamp: 1;
    }

    .likeButtonContainer {
        margin: 0;
    }

    .likeButton {
        margin-right: 5px;
        width: 32px;
        height: 32px;
        padding: 6px;
    }

    .likeHeart {
        width: 18px;
        height: 18px;
    }
}
</style>
